import useResponsiveSidebar from '../hooks/useResponsiveSidebar';
import { ResponsiveSidebar } from './ResponsiveSidebar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDivisions, isFieldRole } from '../utils/divisions';
import { LayoutDashboard, Calendar, Briefcase, Users, Wrench, FileText, LogOut, Building2, ClipboardList, HelpCircle, Shield, CalendarCheck, Clock, Handshake, Home as HomeIcon, Eye, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const sidebar = useResponsiveSidebar('shopSidebarCollapsed');
  const { collapsed, toggleCollapsed } = sidebar;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard' },
    { path: '/schedule', icon: Calendar, label: 'Schedule', permission: 'schedule' },
    { path: '/jobs', icon: Briefcase, label: 'Jobs', permission: 'jobs' },
    { path: '/customers', icon: Building2, label: 'Customers', permission: 'customers' },
    { path: '/technicians', icon: Users, label: 'Technicians', permission: 'technicians' },
    { path: '/equipment', icon: Wrench, label: 'Equipment', permission: 'equipment' },
    { path: '/vendors', icon: Handshake, label: 'Vendors', permission: 'vendors' },
    { path: '/reports', icon: FileText, label: 'Reports', permission: 'reports' },
    { path: '/daily-summary', icon: CalendarCheck, label: 'Daily Summary', permission: 'daily-summary' },
    { path: '/shop-time', icon: Clock, label: 'Shop Time', permission: 'shop-time' },
    { path: '/form-templates', icon: ClipboardList, label: 'Forms', permission: 'form-templates' },
    { path: '/admin', icon: Shield, label: 'Platform Admin', permission: 'admin', superadminOnly: true },
    { path: '/help', icon: HelpCircle, label: 'Help', permission: 'help' },
  ];

  // Fallback role defaults if the server hasn't returned effective_permissions yet
  const ROLE_DEFAULTS = {
    management: ['dashboard', 'schedule', 'jobs', 'job-detail', 'customers', 'technicians', 'equipment', 'vendors', 'reports', 'daily-summary', 'shop-time', 'settings', 'users', 'form-templates', 'help', 'billing', 'admin'],
    foreman: ['dashboard', 'schedule', 'jobs', 'job-detail', 'shop-time', 'daily-summary', 'help', 'customers', 'vendors', 'equipment', 'form-templates', 'reports', 'reports.compliance'],
    technician: ['dashboard', 'schedule', 'jobs', 'job-detail', 'shop-time', 'daily-summary', 'help'],
    field_tech: ['field', 'field.calendar', 'help'],
    field_coordinator: ['field', 'field.calendar', 'field.calendar.edit', 'field.assign', 'field.schedule.manage', 'dashboard', 'schedule', 'jobs', 'job-detail', 'customers', 'technicians', 'equipment', 'vendors', 'reports', 'daily-summary', 'help'],
    field_management: ['field', 'field.calendar', 'field.calendar.edit', 'field.assign', 'field.schedule.manage', 'dashboard', 'schedule', 'jobs', 'job-detail', 'customers', 'technicians', 'equipment', 'vendors', 'reports', 'daily-summary', 'help'],
    admin_safety: ['dashboard', 'schedule', 'jobs', 'job-detail', 'shop-time', 'daily-summary', 'help', 'customers', 'customers.edit', 'vendors', 'vendors.edit', 'equipment', 'equipment.edit', 'form-templates', 'form-templates.edit', 'technicians', 'reports', 'reports.maintenance', 'reports.compliance', 'field', 'field.calendar'],
  };
  const effectivePermissions = (user?.effective_permissions && user.effective_permissions.length > 0)
    ? user.effective_permissions
    : (ROLE_DEFAULTS[user?.role] || []);
  const divisions = getDivisions(user);
  const hasOrgPerm = ['users', 'billing', 'settings'].some((p) => effectivePermissions.includes(p));
  const showHomeLink = divisions.length > 1 || user?.field_locked || hasOrgPerm;
  const shopReadOnly = isFieldRole(user);

  return (
    <div className="flex min-h-screen bg-slate-50" style={{ minHeight: '100dvh' }}>
      {/* Sidebar */}
      <ResponsiveSidebar sidebar={sidebar} testid="shop-sidebar">
        <div className="p-6 border-b border-slate-800 shrink-0">
          <h1 className="text-2xl font-black tracking-tight">ShopManager<span className="text-orange-400">247</span></h1>
          <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Shop Management</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="sidebar-nav">
          {showHomeLink && (
            <Link
              to="/home"
              data-testid="nav-home"
              className="flex items-center gap-3 px-4 py-3 rounded-md transition-colors text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <HomeIcon size={20} />
              <span className="font-medium">Home</span>
            </Link>
          )}
          {navItems.map((item) => {
            if (!effectivePermissions.includes(item.permission)) return null;
            if (item.superadminOnly && !user?.is_superadmin) return null;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive(item.path)
                    ? 'bg-orange-500 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div
          className="p-4 border-t border-slate-800 shrink-0"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="px-4 py-2 mb-2">
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider">{user?.role?.replace(/_/g, ' ')}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </Button>
        </div>
      </ResponsiveSidebar>

      {/* Main content */}
      <main className="flex-1 overflow-auto min-w-0">
        <div className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={toggleCollapsed}
              data-testid="shop-sidebar-toggle"
              title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
              className="p-2 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
            >
              {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
            <h2 className="text-lg md:text-xl font-bold text-slate-900 truncate">
              {navItems.find(item => item.path === location.pathname)?.label
                || { '/users': 'Users', '/billing': 'Billing', '/settings': 'Settings' }[location.pathname]
                || 'RepairHub'}
            </h2>
          </div>
          <NotificationBell />
        </div>
        {shopReadOnly && (
          <div
            data-testid="shop-readonly-banner"
            className="bg-amber-50 border-b border-amber-200 px-8 py-2 text-sm text-amber-800 flex items-center gap-2"
          >
            <Eye size={14} />
            Shop division — read-only view. You can browse shop data but cannot modify it.
          </div>
        )}
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}